import { Head, Link } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { CalendarClock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"
import { programIcon } from "@/lib/program-icons"

type Feature = { icon: string; title: string; body: string }

type LandingProgram = {
  name: string
  slug: string
  tagline: string | null
  headline: string | null
  subheadline: string | null
  cta_label: string | null
  highlight_title: string | null
  highlight_body: string | null
  features: Feature[]
  code_required: boolean
}

export default function ProgramLanding({
  program,
  signed_in,
  member,
}: {
  program: LandingProgram
  signed_in: boolean
  member: boolean
}) {
  const intl = useIntl()
  const headline = program.headline ?? program.name
  const description = program.subheadline ?? intl.formatMessage({ id: "landing.default_description" }, { name: program.name })
  const cta = signed_in
    ? member
      ? { label: intl.formatMessage({ id: "landing.cta_dashboard" }), href: "/dashboard" }
      : { label: intl.formatMessage({ id: "landing.cta_code" }), href: "/profile/programs" }
    : { label: program.cta_label ?? intl.formatMessage({ id: "landing.cta_start" }), href: `/signup?program=${program.slug}` }

  return (
    <>
      <Head title={headline}>
        <meta name="description" content={description} />
        <meta property="og:title" content={intl.formatMessage({ id: "landing.og_title" }, { headline })} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
      </Head>

      <div className="min-h-screen bg-page text-ink-body">
        <header className="border-b border-hairline">
          <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
            <Link href="/" className="flex items-center gap-2 text-ink-display no-underline">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-faded font-display text-sm font-semibold text-accent">
                Q
              </span>
              <span className="font-display text-sm font-semibold">Quiziqal</span>
            </Link>
            <nav className="flex items-center gap-2">
              {signed_in ? (
                <Button asChild size="sm">
                  <Link href="/dashboard">{intl.formatMessage({ id: "landing.dashboard" })}</Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="ghost" size="sm">
                    <Link href="/login">{intl.formatMessage({ id: "landing.log_in" })}</Link>
                  </Button>
                  <Button asChild size="sm">
                    <Link href={`/signup?program=${program.slug}`}>{intl.formatMessage({ id: "landing.sign_up" })}</Link>
                  </Button>
                </>
              )}
            </nav>
          </div>
        </header>

        <main>
          <section className="mx-auto max-w-3xl px-4 pb-16 pt-16 text-center sm:px-6 sm:pt-24">
            {program.tagline && <p className="text-sm text-accent">{program.tagline}</p>}
            <h1 className="mt-3 text-4xl sm:text-5xl">{headline}</h1>
            {program.subheadline && <p className="mx-auto mt-4 max-w-2xl">{program.subheadline}</p>}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg">
                <Link href={cta.href}>{cta.label}</Link>
              </Button>
              {!signed_in && (
                <Button asChild size="lg" variant="secondary">
                  <Link href="/login">{intl.formatMessage({ id: "landing.log_in" })}</Link>
                </Button>
              )}
            </div>
            {!signed_in && program.code_required && (
              <p className="mt-4 text-sm text-ink-muted">{intl.formatMessage({ id: "landing.need_code" })}</p>
            )}
          </section>

          {program.features.length > 0 && (
            <section className="border-t border-hairline bg-surface">
              <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
                <h2 className="text-center text-2xl">{intl.formatMessage({ id: "landing.features_heading" })}</h2>
                <ul className="mt-10 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
                  {program.features.map(({ icon, title, body }) => {
                    const Icon = programIcon(icon)
                    return (
                      <li key={title} className="rounded-xl border border-hairline bg-page p-5 shadow-xs">
                        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-faded text-accent">
                          <Icon className="h-4 w-4" />
                        </span>
                        <h3 className="mt-4">{title}</h3>
                        <p className="mt-1">{body}</p>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </section>
          )}

          {program.highlight_title && program.highlight_body && (
            <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
              <div className="callout callout-accent flex flex-col gap-4 sm:flex-row sm:items-start">
                <CalendarClock className="h-6 w-6 shrink-0 text-accent" />
                <div>
                  <h2 className="text-2xl">{program.highlight_title}</h2>
                  <p className="mt-2">{program.highlight_body}</p>
                </div>
              </div>
            </section>
          )}

          <section className="border-t border-hairline">
            <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
              <h2 className="text-2xl">{intl.formatMessage({ id: "landing.final_heading" })}</h2>
              <div className="mt-6">
                <Button asChild size="lg">
                  <Link href={cta.href}>{cta.label}</Link>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <footer className="border-t border-hairline">
          <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-8 text-xs text-ink-muted sm:flex-row sm:justify-between sm:px-6">
            <span>Quiziqal</span>
            <LanguageSwitcher id="footer-language-switcher" className="w-auto text-xs sm:order-last" />
            <span>
              {intl.formatMessage({ id: "landing.disclaimer" })}
            </span>
          </div>
        </footer>
      </div>
    </>
  )
}
